import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { Icon } from '../../foundation/primitives';
import { JudgementModal, StartGate, FailOverlay, MinigameHud } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

// 既定パズル＝従来のハードコードデモ。config 不在（または不正）時はこの内容に
// フォールバックし、ゼロ config の見た目・挙動を従来と不変に保つ（R0 回帰安全）。
const DEFAULT_CORRECT_CODE = [2, 4, 7];
const DEFAULT_CODE_HINTS = [
  { text: '窓の枠は 2 つに分かれている', highlightValue: 2 },
  { text: '本棚の段数は 4 段', highlightValue: 4 },
  { text: '時計の針は 7 を指している', highlightValue: 7 },
];
// R1: 旧既定「🔐 金庫解錠」の絵文字は HUD/StartGate の Lock アイコンに役割を譲り題のみ残す
const DEFAULT_TITLE = '金庫解錠';
const DEFAULT_SUCCESS_TITLE = '解錠成功！';
// R1: 既定文言は常体（敬体「開きました」を排除）
const DEFAULT_SUCCESS_SUBTITLE = '金庫が開いた';
// R1: 開始ゲートの既定説明（常体）。config.instructionText があればそちらを使う
const DEFAULT_GATE_INSTRUCTION = 'ヒントから数字を読み解き、ダイヤルを合わせて解錠せよ';
const JUDGE_TOAST_MS = 1100;

const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;

// 桁数は correctCode.length が単一の正。schema の dialCount と食い違っても correctCode を優先する
const isValidCode = (value) =>
  Array.isArray(value) && value.length > 0 && value.every((n) => Number.isInteger(n) && n >= 0 && n <= 9);

/** ヒント文中の highlightValue（最後の出現箇所）を強調スパンで挟んで描画する */
const renderHintText = (hint) => {
  const text = isNonEmptyString(hint.text) ? hint.text : '';
  if (!Number.isInteger(hint.highlightValue)) return text;
  const token = String(hint.highlightValue);
  const at = text.lastIndexOf(token);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <span className="t03-hint-em">{token}</span>
      {text.slice(at + token.length)}
    </>
  );
};

const Template03Cipher = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_cipher_03', templateId = 3 }) => {
  // 確立パターン: configKey（JSON 文字列）で config 実体の変化を検知し、
  // 変化時のみ state をリセットする（毎レンダーの参照変化では揺らさない）。
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const config = hostConfig ?? {};
    const correctCode = isValidCode(config.correctCode) ? config.correctCode : DEFAULT_CORRECT_CODE;
    const configHints = Array.isArray(config.codeHints)
      ? config.codeHints.filter((hint) => hint && isNonEmptyString(hint.text))
      : [];

    // dialStyle は config 由来の直接色のため inline style として適用する
    const dialStyle = config.dialStyle && typeof config.dialStyle === 'object' ? config.dialStyle : null;
    const wheelStyle = {};
    if (dialStyle && isNonEmptyString(dialStyle.backgroundColor)) wheelStyle.backgroundColor = dialStyle.backgroundColor;
    if (dialStyle && isNonEmptyString(dialStyle.textColor)) wheelStyle.color = dialStyle.textColor;

    return {
      correctCode,
      codeHints: configHints.length > 0 ? configHints : DEFAULT_CODE_HINTS,
      showMatchCount: config.showMatchCount !== false,
      title: isNonEmptyString(config.title) ? config.title : DEFAULT_TITLE,
      instructionText: isNonEmptyString(config.instructionText) ? config.instructionText : null,
      wheelStyle: Object.keys(wheelStyle).length > 0 ? wheelStyle : undefined,
      successTitle: isNonEmptyString(config.successMessage?.title) ? config.successMessage.title : DEFAULT_SUCCESS_TITLE,
      successSubtitle: isNonEmptyString(config.successMessage?.subtitle)
        ? config.successMessage.subtitle
        : DEFAULT_SUCCESS_SUBTITLE,
      // R1: 失敗オーバーレイ文言（BaseConfig.failMessage）。null なら FailOverlay の既定 or
      // 失敗理由別の既定 subtitle を使う
      failTitle: isNonEmptyString(config.failMessage?.title) ? config.failMessage.title : null,
      failSubtitle: isNonEmptyString(config.failMessage?.subtitle) ? config.failMessage.subtitle : null,
      failAllowRetry: config.failMessage?.allowRetry !== false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  const dialCount = resolvedConfig.correctCode.length;
  const [dials, setDials] = useState(() => Array(dialCount).fill(0));
  const [gameState, setGameState] = useState('playing');
  // 一致数は「解錠」押下時にのみ更新する（ホイール単位のリアルタイム一致開示は
  // 総当たりで解けてしまうため R0 で廃止。どの桁が一致かは開示しない）。
  const [judgedMatchCount, setJudgedMatchCount] = useState(null);
  const [judgeToast, setJudgeToast] = useState(null);
  const [shaking, setShaking] = useState(false);
  // R1: 開始ゲート（体験文法統一）。step.ui.showTutorial === false の時のみ最初から閉じる
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  // R1: ミス数（誤答の解錠試行）・失敗理由・やり直し回数（retryCount は全 submit payload に同梱）
  const [mistakes, setMistakes] = useState(0);
  const [failReason, setFailReason] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const backgroundStyle = useMinigameBackground(hostConfig);
  const toastTimerRef = useRef(null);
  const lastConfigKeyRef = useRef(configKey);

  // R1 ミス上限: config.maxMistakes 優先 → hostMeta.maxMistakes（step 側）。step schema は
  // 0 を「未設定」とするため、どちらも >0 のみ採用。未設定なら無制限（現行どおり）。
  const mistakeLimit = Number.isFinite(hostConfig?.maxMistakes) && hostConfig.maxMistakes > 0
    ? Math.floor(hostConfig.maxMistakes)
    : Number.isFinite(hostMeta?.maxMistakes) && hostMeta.maxMistakes > 0
      ? Math.floor(hostMeta.maxMistakes)
      : null;
  // R1 実動タイマー: config.timeLimitMs 優先 → hostMeta.timeLimitMs。0 以下は useMinigameTimer
  // 側で無効化＝表示も強制も無し（ゼロ config 回帰安全）。
  // config.showTimer === false は「表示のみ」抑止で、満了 fail の強制は維持する。
  const limitMs = Number.isFinite(hostConfig?.timeLimitMs)
    ? hostConfig.timeLimitMs
    : Number.isFinite(hostMeta?.timeLimitMs)
      ? hostMeta.timeLimitMs
      : null;
  const showTimerChip = hostConfig?.showTimer !== false;
  // R1: ゲート表示中・決着後は止める。満了で fail 状態へ
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs,
    running: !gateOpen && gameState === 'playing',
    onExpire: () => {
      if (gameState !== 'playing') return;
      setFailReason('timeout');
      setGameState('fail');
    }
  });

  // config 実体が差し替わった時のみ進行状態をリセット（初回マウントでは何もしない）
  useEffect(() => {
    if (lastConfigKeyRef.current === configKey) return;
    lastConfigKeyRef.current = configKey;
    setDials(Array(resolvedConfig.correctCode.length).fill(0));
    setGameState('playing');
    setJudgedMatchCount(null);
    setJudgeToast(null);
    setShaking(false);
    setMistakes(0);
    setFailReason(null);
    setRetryCount(0);
    resetTimer();
    resetSubmission();
  }, [configKey, resolvedConfig, resetSubmission, resetTimer]);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const changeDial = (index, direction) => {
    if (gameState !== 'playing') return;
    setDials((prev) => {
      const next = Array.from({ length: dialCount }, (_, i) => prev[i] ?? 0);
      next[index] = (next[index] + direction + 10) % 10;
      return next;
    });
  };

  const showJudgeToast = (message) => {
    setJudgeToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setJudgeToast(null), JUDGE_TOAST_MS);
  };

  const checkCode = () => {
    if (gameState !== 'playing') return;
    const matchCount = resolvedConfig.correctCode.filter((digit, index) => (dials[index] ?? 0) === digit).length;
    if (matchCount === dialCount) {
      setGameState('success');
      return;
    }
    // R1: 誤答の解錠試行を 1 ミスと数え、上限到達で fail（FailOverlay に委ねるため
    // 到達時はトースト/シェイクを出さない）。R0 の判定時フィードバック自体は不変
    const nextMistakes = mistakes + 1;
    setMistakes(nextMistakes);
    if (mistakeLimit != null && nextMistakes >= mistakeLimit) {
      setFailReason('mistakes');
      setGameState('fail');
      return;
    }
    if (resolvedConfig.showMatchCount) {
      // 集計のみ開示（どの桁かは明かさない）
      setJudgedMatchCount(matchCount);
      setShaking(true);
      showJudgeToast(`一致 ${matchCount}/${dialCount}`);
    } else {
      showJudgeToast('開かない…');
    }
  };

  const reset = () => {
    setDials(Array(dialCount).fill(0));
    setGameState('playing');
    setJudgedMatchCount(null);
    setJudgeToast(null);
    setShaking(false);
    setMistakes(0);
    setFailReason(null);
    resetSubmission();
  };

  return (
    <div className="t03-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      <div className="t03-bg-overlay absolute inset-0" />

      {/* R1: 共通 HUD（旧「🔐 金庫解錠」タイトルチップ＋onClick の無い飾りメニューを撤去して置換。
          錠前は Lock アイコン＝lucide 実在検証済み）。タイマー/ミスは設定時のみ表示
          （showTimer=false は表示のみ抑止・満了 fail は維持）。?＝開始ゲート再表示 */}
      <MinigameHud
        title={resolvedConfig.title}
        icon="Lock"
        timer={showTimerChip && remainingMs != null ? { remainingMs, totalMs: limitMs } : null}
        mistakes={mistakeLimit != null ? { current: mistakes, max: mistakeLimit } : null}
        onHelp={() => setGateOpen(true)}
      />

      {judgeToast && (
        <div className="t03-judge-toast absolute top-20 left-1/2 -translate-x-1/2 z-40 px-5 py-2 rounded-xl text-sm font-bold">
          {judgeToast}
        </div>
      )}

      <div className="absolute inset-0 pt-16 pb-52 flex items-center justify-center">
        <div
          className={`t03-dial-box rounded-2xl p-6 border-4 ${shaking ? 't03-shake' : ''}`}
          onAnimationEnd={(e) => {
            if (e.target === e.currentTarget) setShaking(false);
          }}
        >
          <div className="flex flex-wrap justify-center gap-4">
            {Array.from({ length: dialCount }, (_, index) => (
              <div key={index} className="flex flex-col items-center">
                <button
                  className="t03-dial-step w-12 h-10 rounded-t-lg flex items-center justify-center"
                  onClick={() => changeDial(index, 1)}
                >
                  <ChevronUp className="t03-dial-step-icon w-6 h-6" />
                </button>
                <div
                  className="t03-wheel t03-wheel--idle w-16 h-20 flex items-center justify-center text-4xl font-mono font-bold border-2"
                  style={resolvedConfig.wheelStyle}
                >
                  {dials[index] ?? 0}
                </div>
                <button
                  className="t03-dial-step w-12 h-10 rounded-b-lg flex items-center justify-center"
                  onClick={() => changeDial(index, -1)}
                >
                  <ChevronDown className="t03-dial-step-icon w-6 h-6" />
                </button>
              </div>
            ))}
          </div>
          <button
            className="t03-unlock-btn w-full mt-4 font-bold py-3 rounded-xl transition-colors"
            onClick={checkCode}
          >
            解錠
          </button>
        </div>
      </div>

      <div className="t03-hint-wrap absolute bottom-0 left-0 right-0 z-30 pt-6 pb-6 px-4">
        <div className="t03-hint-panel border rounded-xl p-4">
          <p className="t03-hint-label text-sm font-bold mb-2">💡 ヒント</p>
          {resolvedConfig.instructionText && (
            <p className="t03-instruction text-sm mb-2">{resolvedConfig.instructionText}</p>
          )}
          <div className="space-y-1 t03-hint-text text-sm">
            {resolvedConfig.codeHints.map((hint, index) => (
              <p key={index}>• {renderHintText(hint)}</p>
            ))}
          </div>
          {resolvedConfig.showMatchCount && (
            <div className="mt-3 flex items-center gap-2">
              <span className="t03-match-label text-xs">一致:</span>
              <div className="flex gap-1">
                {Array.from({ length: dialCount }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full t03-match-dot ${judgedMatchCount != null && i < judgedMatchCount ? 't03-match-dot--on' : 't03-match-dot--off'}`}
                  />
                ))}
              </div>
              {judgedMatchCount != null && (
                <span className="t03-match-label text-xs font-mono">{judgedMatchCount}/{dialCount}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          R1: payload に retryCount を同梱） */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successTitle}
        description={resolvedConfig.successSubtitle}
        icon={<Icon name="LockOpen" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />

      {/* R1: 失敗（タイマー満了 / ミス上限到達）は semantic FailOverlay に統一。
          文言は config.failMessage（title/subtitle/allowRetry）を優先し、不在時は
          FailOverlay 既定＋失敗理由別の subtitle。未整理のまま進む → submit('fail') */}
      {gameState === 'fail' && (
        <FailOverlay
          title={resolvedConfig.failTitle ?? undefined}
          subtitle={resolvedConfig.failSubtitle ?? (failReason === 'timeout' ? '時間切れ' : 'ミスが多すぎた')}
          allowRetry={resolvedConfig.failAllowRetry}
          onRetry={() => {
            setRetryCount((count) => count + 1);
            reset();
            resetTimer();
          }}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* R1: 開始ゲート（体験文法統一）。開始までゲーム操作・タイマーは始まらない。
          HUD の ? で随時再表示（表示中はタイマー一時停止）。説明は config.instructionText
          （ヒントパネル併記は R0 仕様のまま）?? テンプレ既定の一行 */}
      {gateOpen && (
        <StartGate
          title={resolvedConfig.title}
          icon="Lock"
          instruction={resolvedConfig.instructionText ?? DEFAULT_GATE_INSTRUCTION}
          onStart={() => setGateOpen(false)}
        />
      )}
    </div>
  );
};

export default Template03Cipher;
